import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Trash2, LogOut, Upload, Image as ImageIcon, Save, Plus, ExternalLink, Star } from "lucide-react";

const CATEGORIES = ["renovation", "painting", "construction"];

function GalleryManager() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ title: "", category: "renovation", image_url: "", caption: "" });
    const [loading, setLoading] = useState(false);

    const load = async () => {
        const { data } = await api.get("/gallery");
        setItems(data || []);
    };
    useEffect(() => { load(); }, []);

    const onFile = (file) => {
        if (!file) return;
        if (file.size > 4 * 1024 * 1024) {
            toast.error("Image too large (max 4MB). Try a compressed version.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setForm((f) => ({ ...f, image_url: reader.result }));
        reader.readAsDataURL(file);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title || !form.image_url) {
            toast.error("Title and image are required.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/admin/gallery", form);
            toast.success("Image added.");
            setForm({ title: "", category: "renovation", image_url: "", caption: "" });
            await load();
        } catch (_e) {
            toast.error("Failed to add image.");
        } finally {
            setLoading(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/admin/gallery/${id}`);
            toast.success("Removed.");
            setItems((prev) => prev.filter((i) => i.id !== id));
        } catch (_e) {
            toast.error("Failed to delete.");
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Card className="lg:col-span-1 p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Add New</div>
                <form onSubmit={submit} className="space-y-4" data-testid="gallery-form">
                    <div>
                        <Label>Title</Label>
                        <Input data-testid="gallery-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Kitchen remodel" />
                    </div>
                    <div>
                        <Label>Category</Label>
                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                            <SelectTrigger data-testid="gallery-category"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Image (upload or paste URL)</Label>
                        <div className="mt-1 flex items-center gap-3">
                            <label className="inline-flex items-center gap-2 border border-dashed border-neutral-300 px-4 py-2 rounded cursor-pointer hover:border-[color:var(--brand-blue)] text-sm">
                                <Upload className="h-4 w-4" /> Upload
                                <input data-testid="gallery-file" type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
                            </label>
                        </div>
                        <Input className="mt-2" data-testid="gallery-url" value={form.image_url.startsWith("data:") ? "" : form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Or paste image URL" />
                        {form.image_url && (
                            <div className="mt-3 relative aspect-video overflow-hidden bg-neutral-100 rounded">
                                <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
                            </div>
                        )}
                    </div>
                    <div>
                        <Label>Caption</Label>
                        <Input data-testid="gallery-caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="Optional short caption" />
                    </div>
                    <Button data-testid="gallery-submit" type="submit" disabled={loading} className="w-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)]">
                        <Plus className="h-4 w-4 mr-2" /> {loading ? "Saving…" : "Add to gallery"}
                    </Button>
                </form>
            </Card>

            <div className="lg:col-span-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Current items ({items.length})</div>
                {items.length === 0 ? (
                    <Card className="p-10 text-center text-neutral-500">
                        <ImageIcon className="h-8 w-8 mx-auto mb-3 text-neutral-400" />
                        No images yet. Add your first project photo.
                    </Card>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {items.map((it) => (
                            <Card key={it.id} className="overflow-hidden group" data-testid={`gallery-card-${it.id}`}>
                                <div className="aspect-square bg-neutral-100 relative">
                                    <img src={it.image_url} alt={it.title} className="w-full h-full object-cover" />
                                    <button
                                        onClick={() => remove(it.id)}
                                        className="absolute top-2 right-2 h-8 w-8 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                        title="Delete"
                                        data-testid={`gallery-delete-${it.id}`}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="p-3">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{it.category}</div>
                                    <div className="mt-1 font-display font-bold text-sm text-neutral-900 truncate">{it.title}</div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function TestimonialsManager() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ name: "", location: "Baton Rouge, LA", quote: "", rating: 5 });
    const [loading, setLoading] = useState(false);

    const load = async () => {
        const { data } = await api.get("/testimonials");
        setItems(data || []);
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.quote) return toast.error("Name and quote are required.");
        setLoading(true);
        try {
            await api.post("/admin/testimonials", form);
            toast.success("Testimonial added.");
            setForm({ name: "", location: "Baton Rouge, LA", quote: "", rating: 5 });
            await load();
        } catch (_e) {
            toast.error("Failed to add.");
        } finally {
            setLoading(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/admin/testimonials/${id}`);
            setItems((p) => p.filter((i) => i.id !== id));
            toast.success("Removed.");
        } catch (_e) {
            toast.error("Failed to delete.");
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Card className="lg:col-span-1 p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Add Testimonial</div>
                <form onSubmit={submit} className="space-y-4" data-testid="testimonial-form">
                    <div>
                        <Label>Client name</Label>
                        <Input data-testid="testimonial-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div>
                        <Label>Location</Label>
                        <Input data-testid="testimonial-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                    </div>
                    <div>
                        <Label>Quote</Label>
                        <Textarea data-testid="testimonial-quote" rows={4} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} />
                    </div>
                    <div>
                        <Label>Rating (1-5)</Label>
                        <Input data-testid="testimonial-rating" type="number" min={1} max={5} value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value || "5") })} />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="testimonial-submit" className="w-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)]">
                        <Plus className="h-4 w-4 mr-2" /> {loading ? "Saving…" : "Add testimonial"}
                    </Button>
                </form>
            </Card>

            <div className="lg:col-span-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Current testimonials ({items.length})</div>
                <div className="space-y-4">
                    {items.map((t) => (
                        <Card key={t.id} className="p-6" data-testid={`testimonial-card-${t.id}`}>
                            <div className="flex justify-between items-start gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 text-[color:var(--brand-blue)]">
                                        {Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} className="h-3.5 w-3.5 fill-current" />)}
                                    </div>
                                    <p className="mt-3 text-sm text-neutral-800 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                                    <div className="mt-3 text-xs text-neutral-500">
                                        <span className="font-semibold text-neutral-800">{t.name}</span> — {t.location}
                                    </div>
                                </div>
                                <Button variant="ghost" size="icon" onClick={() => remove(t.id)} data-testid={`testimonial-delete-${t.id}`}>
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ContentManager() {
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/content");
            setForm(data);
        })();
    }, []);

    if (!form) return <div className="text-neutral-500 text-sm">Loading…</div>;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setLoading(true);
        try {
            await api.put("/admin/content", form);
            toast.success("Site content updated.");
        } catch (_e) {
            toast.error("Failed to save.");
        } finally {
            setLoading(false);
        }
    };

    const onLogoFile = (file) => {
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) return toast.error("Logo too large (max 2MB).");
        const reader = new FileReader();
        reader.onload = () => set("logo_url", reader.result);
        reader.readAsDataURL(file);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Hero</div>
                <div className="space-y-4">
                    <div><Label>Eyebrow</Label><Input data-testid="content-eyebrow" value={form.hero_eyebrow || ""} onChange={(e) => set("hero_eyebrow", e.target.value)} /></div>
                    <div><Label>Subtitle</Label><Textarea data-testid="content-subtitle" rows={3} value={form.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} /></div>
                    <div>
                        <Label>Logo URL</Label>
                        <Input data-testid="content-logo-url" value={form.logo_url?.startsWith("data:") ? "" : (form.logo_url || "")} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://..." />
                        <label className="mt-2 inline-flex items-center gap-2 border border-dashed border-neutral-300 px-3 py-2 rounded cursor-pointer hover:border-[color:var(--brand-blue)] text-sm">
                            <Upload className="h-4 w-4" /> Upload new logo
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => onLogoFile(e.target.files?.[0])} />
                        </label>
                        {form.logo_url && (
                            <div className="mt-3 h-24 bg-neutral-100 rounded flex items-center justify-center p-2">
                                <img src={form.logo_url} alt="logo" className="max-h-full max-w-full object-contain" />
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            <Card className="p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Contact & About</div>
                <div className="space-y-4">
                    <div><Label>Phone (display)</Label><Input data-testid="content-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
                    <div><Label>Phone (tel: link)</Label><Input data-testid="content-phone-raw" value={form.phone_raw || ""} onChange={(e) => set("phone_raw", e.target.value)} placeholder="+12256202635" /></div>
                    <div><Label>WhatsApp number</Label><Input data-testid="content-whatsapp" value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} placeholder="12256202635 (no + or spaces)" /></div>
                    <div><Label>Service area</Label><Input data-testid="content-area" value={form.service_area || ""} onChange={(e) => set("service_area", e.target.value)} /></div>
                    <div><Label>Business hours</Label><Input data-testid="content-hours" value={form.hours || ""} onChange={(e) => set("hours", e.target.value)} placeholder="Mon – Sat · 7:00 AM – 6:00 PM" /></div>
                    <div><Label>About paragraph</Label><Textarea data-testid="content-about" rows={4} value={form.about_paragraph || ""} onChange={(e) => set("about_paragraph", e.target.value)} /></div>
                </div>
            </Card>

            <div className="lg:col-span-2 flex justify-end">
                <Button onClick={save} disabled={loading} data-testid="content-save" className="bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)]">
                    <Save className="h-4 w-4 mr-2" /> {loading ? "Saving…" : "Save changes"}
                </Button>
            </div>
        </div>
    );
}

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        if (user === false) nav("/admin/login", { replace: true });
    }, [user, nav]);

    if (!user || user === false) {
        return <div className="min-h-screen flex items-center justify-center text-neutral-500 text-sm">Checking session…</div>;
    }

    return (
        <div className="min-h-screen bg-[color:var(--bg-secondary)]" data-testid="admin-dashboard">
            <header className="bg-[color:var(--surface-dark)] text-white">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/" className="font-display font-bold text-sm tracking-tight hover:text-[color:var(--brand-blue)]">
                            HOME RENOVATION · STEVEN R. CONSTRUCTION
                        </Link>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">Admin Panel</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/" className="text-xs text-white/70 hover:text-white inline-flex items-center gap-1">
                            View site <ExternalLink className="h-3 w-3" />
                        </Link>
                        <Button size="sm" variant="secondary" onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout">
                            <LogOut className="h-3.5 w-3.5 mr-2" /> Logout
                        </Button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-[1440px] px-6 md:px-10 py-10">
                <div className="mb-8">
                    <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">Welcome back, {user.name}</div>
                    <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter text-neutral-900">Site Control</h1>
                </div>

                <Tabs defaultValue="gallery" className="w-full">
                    <TabsList data-testid="admin-tabs">
                        <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
                        <TabsTrigger value="testimonials" data-testid="tab-testimonials">Testimonials</TabsTrigger>
                        <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
                    </TabsList>
                    <TabsContent value="gallery" className="mt-8"><GalleryManager /></TabsContent>
                    <TabsContent value="testimonials" className="mt-8"><TestimonialsManager /></TabsContent>
                    <TabsContent value="content" className="mt-8"><ContentManager /></TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
